import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, API } from "@/lib/api";
import { rupees, can } from "@/lib/helpers";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Plus, Handshake, PencilSimple, HandCoins, Receipt, Key } from "@phosphor-icons/react";

const empty = {
  name: "", mobile: "", alt_mobile: "", email: "", address: "", city: "", pan: "", aadhaar: "",
  gstin: "", bank_account: "", upi_id: "", agent_type: "Individual Agent", status: "Active",
  commission_percent: 1, commission_base: "net", notes: "",
};

export default function Agents() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ types: [], statuses: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [settle, setSettle] = useState({ amount: "", method: "Bank Transfer", tds: 0, reference: "", remarks: "" });
  const [loginForm, setLoginForm] = useState({ username: "", password: "", confirm_password: "" });
  const [createLogin, setCreateLogin] = useState({ username: "", password: "", confirm_password: "" });
  const canCreate = can(user, "agents", "create");
  const canEdit = can(user, "agents", "edit");
  const canSettle = can(user, "agents", "settle");
  const canApprove = can(user, "agents", "approve");
  const canSetLogin = can(user, "users", "create") && can(user, "agents", "edit");

  const load = useCallback(async () => { setRows((await api.get("/agents")).data); }, []);
  useEffect(() => {
    load();
    api.get("/agents/meta/options").then(({ data }) => setOpts(data));
    api.get("/agents/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  }, [load]);
  const openReceipt = async (sid) => {
    const token = localStorage.getItem("og_token");
    const r = await fetch(`${API}/agents/settlements/${sid}/receipt.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) window.open(URL.createObjectURL(await r.blob()), "_blank"); else toast.error("Could not open receipt");
  };

  const openNew = () => { setForm(empty); setCreateLogin({ username: "", password: "", confirm_password: "" }); setEditId(null); setOpen(true); };
  const openEdit = (a, e) => { e?.stopPropagation(); setForm({ ...empty, ...a }); setEditId(a.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, commission_percent: Number(form.commission_percent || 0) };
      if (editId) { const { id, agent_code, created_at, ...p } = payload; await api.put(`/agents/${editId}`, p); toast.success("Agent updated"); }
      else {
        const { data } = await api.post("/agents", payload);
        if (createLogin.username && createLogin.password) {
          if (createLogin.password !== createLogin.confirm_password) {
            toast.error(`Agent created (${data.agent_code}), but login passwords did not match — set it from the agent later`);
          } else {
            try { await api.post(`/agents/${data.id}/login`, createLogin); toast.success(`Agent created — ${data.agent_code} · login: ${createLogin.username}`); }
            catch (le) { toast.error(`Agent created (${data.agent_code}), but login not set: ${apiError(le)}`); }
          }
        } else { toast.success(`Agent created — ${data.agent_code}`); }
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (id) => { const { data } = await api.get(`/agents/${id}`); setDetail(data); setSettle({ amount: String(data.summary.pending || ""), method: "Bank Transfer", tds: 0, reference: "", remarks: "" }); setLoginForm({ username: data.login_username || "", password: "", confirm_password: "" }); };
  const approve = async (cid) => { try { await api.post(`/agents/commissions/${cid}/approve`); toast.success("Approved"); openDetail(detail.agent.id); } catch (e) { toast.error(apiError(e)); } };
  const doSettle = async () => {
    try { await api.post(`/agents/${detail.agent.id}/settle`, { ...settle, amount: Number(settle.amount), tds: Number(settle.tds || 0) }); toast.success("Settlement recorded"); openDetail(detail.agent.id); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveLogin = async () => {
    if (!loginForm.username || !loginForm.password) return toast.error("Username and password are required");
    if (loginForm.password !== loginForm.confirm_password) return toast.error("Passwords do not match");
    try { const { data } = await api.post(`/agents/${detail.agent.id}/login`, loginForm); toast.success(`Login saved — ${data.username}`); openDetail(detail.agent.id); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Agents & Referrals" subtitle="Referral agents, commission ledger and settlement.">
        {canCreate && <Button onClick={openNew} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-agent-btn"><Plus size={16} className="mr-1" /> New Agent</Button>}
      </PageHeader>

      <Tabs defaultValue="directory">
        <TabsList className="bg-[#F2EBE1] mb-4">
          <TabsTrigger value="directory" data-testid="agent-tab-directory">Directory</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="agent-tab-analytics">Analytics</TabsTrigger>
        </TabsList>
        <TabsContent value="directory">
      <Card className="bg-white border-[#E6E2D6] shadow-sm">
        {rows.length === 0 ? <EmptyState label="No agents yet" /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Mobile</TableHead>
              <TableHead>Type</TableHead><TableHead className="text-right">Comm %</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} className="cursor-pointer" onClick={() => openDetail(a.id)} data-testid={`agent-row-${a.agent_code}`}>
                  <TableCell className="font-medium">{a.agent_code}</TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell className="text-sm">{a.mobile}</TableCell>
                  <TableCell className="text-sm">{a.agent_type}</TableCell>
                  <TableCell className="text-right tabular-nums">{a.commission_percent}% ({a.commission_base})</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>{canEdit && <Button variant="ghost" size="icon" onClick={(e) => openEdit(a, e)} data-testid={`edit-agent-${a.agent_code}`}><PencilSimple size={16} /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
        </TabsContent>

        <TabsContent value="analytics">
          {!analytics ? <EmptyState label="Loading analytics…" /> : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                {[["Agents", analytics.kpis.total_agents], ["Active", analytics.kpis.active_agents], ["Agent Sales", rupees(analytics.kpis.total_sales)], ["Commission", rupees(analytics.kpis.total_commission)], ["Paid", rupees(analytics.kpis.paid)], ["Pending", rupees(analytics.kpis.pending)], ["Reversed", rupees(analytics.kpis.reversed)]].map(([l, v]) => (
                  <Card key={l} className="p-3 bg-white border-[#E6E2D6] shadow-sm"><div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">{l}</div><div className="text-base font-medium tabular-nums mt-1">{v}</div></Card>
                ))}
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <Card className="p-4 bg-white border-[#E6E2D6] shadow-sm">
                  <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Top Agents by Sales</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analytics.top_agents}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEE8DC" />
                      <XAxis dataKey="agent_code" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => rupees(v)} />
                      <Bar dataKey="sales" fill="#C5A059" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Card className="p-4 bg-white border-[#E6E2D6] shadow-sm">
                  <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Commission Trend</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={analytics.commission_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEE8DC" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => rupees(v)} />
                      <Line type="monotone" dataKey="commission" stroke="#2D6A4F" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
                <Card className="p-4 bg-white border-[#E6E2D6] shadow-sm lg:col-span-2">
                  <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Branch-wise Agent Performance</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analytics.branch_wise}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEE8DC" />
                      <XAxis dataKey="branch" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => rupees(v)} />
                      <Bar dataKey="sales" fill="#C5A059" radius={[4, 4, 0, 0]} name="Sales" />
                      <Bar dataKey="commission" fill="#8A6D2F" radius={[4, 4, 0, 0]} name="Commission" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create / edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Agent" : "New Agent"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["name", "Name"], ["mobile", "Mobile"], ["alt_mobile", "Alt Mobile"], ["email", "Email"], ["city", "City"], ["pan", "PAN"], ["aadhaar", "Aadhaar/KYC"], ["gstin", "GSTIN"], ["bank_account", "Bank Account"], ["upi_id", "UPI ID"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs text-[#6B6B6B]">{l}</Label><Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid={`af-${k}`} /></div>
            ))}
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Address</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-address" /></div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Agent Type</Label>
              <Select value={form.agent_type} onValueChange={(v) => setForm({ ...form, agent_type: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{opts.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{opts.statuses.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#6B6B6B]">Commission %</Label><Input type="number" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-comm-pct" /></div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Commission Base</Label>
              <Select value={form.commission_base} onValueChange={(v) => setForm({ ...form, commission_base: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-comm-base"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="net">Net (after discount)</SelectItem>
                  <SelectItem value="gross">Gross (before discount)</SelectItem>
                  <SelectItem value="making">Making Charges</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {!editId && canSetLogin && (
            <div className="bg-[#FDFBF7] border border-[#E6E2D6] rounded-md p-3 mt-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] flex items-center gap-1 mb-2"><Key size={13} /> Agent Login (optional) <span className="normal-case tracking-normal text-[#8A8A8A]">— so the agent can view their ledger & referrals</span></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs text-[#6B6B6B]">Username</Label><Input value={createLogin.username} onChange={(e) => setCreateLogin({ ...createLogin, username: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-login-username" /></div>
                <div><Label className="text-xs text-[#6B6B6B]">Password</Label><Input type="password" value={createLogin.password} onChange={(e) => setCreateLogin({ ...createLogin, password: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-login-password" /></div>
                <div><Label className="text-xs text-[#6B6B6B]">Confirm</Label><Input type="password" value={createLogin.confirm_password} onChange={(e) => setCreateLogin({ ...createLogin, confirm_password: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="af-login-confirm" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="af-save">{editId ? "Save Changes" : "Create Agent"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{detail.agent.agent_code} — {detail.agent.name}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-4 gap-2">
                {[["Sales", rupees(detail.summary.total_sales)], ["Commission", rupees(detail.summary.total_commission)], ["Paid", rupees(detail.summary.paid)], ["Pending", rupees(detail.summary.pending)]].map(([l, v]) => (
                  <div key={l} className="bg-[#F8F6F0] rounded-md p-3 text-center"><div className="text-[10px] uppercase text-[#8A8A8A]">{l}</div><div className="font-medium tabular-nums text-sm">{v}</div></div>
                ))}
              </div>

              {canSettle && detail.summary.pending > 0 && (
                <div className="bg-[#FDFBF7] border border-[#E6E2D6] rounded-md p-3 mt-2 grid grid-cols-2 gap-2 items-end">
                  <div><Label className="text-xs text-[#6B6B6B]">Amount ₹</Label><Input type="number" value={settle.amount} onChange={(e) => setSettle({ ...settle, amount: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="settle-amount" /></div>
                  <div>
                    <Label className="text-xs text-[#6B6B6B]">Method</Label>
                    <Select value={settle.method} onValueChange={(v) => setSettle({ ...settle, method: v })}>
                      <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="settle-method"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{["Bank Transfer", "UPI", "Cheque", "Cash", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs text-[#6B6B6B]">TDS ₹</Label><Input type="number" value={settle.tds} onChange={(e) => setSettle({ ...settle, tds: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="settle-tds" /></div>
                  <Button onClick={doSettle} className="bg-[#2D6A4F] hover:bg-[#245741] text-white" data-testid="settle-btn"><HandCoins size={16} className="mr-1" /> Record Settlement</Button>
                </div>
              )}

              <div className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] mt-2">Commission Ledger</div>
              <div className="max-h-56 overflow-y-auto border border-[#E6E2D6] rounded-md">
                <Table>
                  <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead className="text-right">Eligible</TableHead><TableHead className="text-right">Comm</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{detail.commissions.map((c) => (
                    <TableRow key={c.id} data-testid={`comm-${c.id}`}>
                      <TableCell className="text-xs">{c.invoice_no}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{rupees(c.eligible_amount)}</TableCell>
                      <TableCell className="text-right tabular-nums">{rupees(c.commission_amount)}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell>{canApprove && c.status === "calculated" && <Button size="sm" variant="ghost" onClick={() => approve(c.id)} data-testid={`approve-${c.id}`}>Approve</Button>}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>

              {detail.settlements && detail.settlements.length > 0 && (
                <>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] mt-3">Settlements</div>
                  <div className="max-h-40 overflow-y-auto border border-[#E6E2D6] rounded-md">
                    <Table>
                      <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Method</TableHead><TableHead className="text-right">Net Paid</TableHead><TableHead></TableHead></TableRow></TableHeader>
                      <TableBody>{detail.settlements.map((s) => (
                        <TableRow key={s.id} data-testid={`settlement-${s.id}`}>
                          <TableCell className="text-xs">{(s.created_at || "").slice(0, 10)}</TableCell>
                          <TableCell className="text-xs">{s.method}</TableCell>
                          <TableCell className="text-right tabular-nums">{rupees(s.net_paid ?? s.amount)}</TableCell>
                          <TableCell><Button size="sm" variant="ghost" onClick={() => openReceipt(s.id)} data-testid={`receipt-${s.id}`}><Receipt size={15} className="mr-1" /> Receipt</Button></TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  </div>
                </>
              )}
              {canSetLogin && (
                <div className="bg-[#FDFBF7] border border-[#E6E2D6] rounded-md p-3 mt-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#6B6B6B] flex items-center gap-1 mb-2"><Key size={13} /> Agent Login {detail.has_login && <span className="text-[#2D6A4F] normal-case tracking-normal">· active ({detail.login_username})</span>}</div>
                  <div className="grid grid-cols-3 gap-2 items-end">
                    <div><Label className="text-xs text-[#6B6B6B]">Username</Label><Input value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="agent-login-username" /></div>
                    <div><Label className="text-xs text-[#6B6B6B]">Password</Label><Input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="agent-login-password" /></div>
                    <div><Label className="text-xs text-[#6B6B6B]">Confirm</Label><Input type="password" value={loginForm.confirm_password} onChange={(e) => setLoginForm({ ...loginForm, confirm_password: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="agent-login-confirm" /></div>
                  </div>
                  <Button onClick={saveLogin} className="mt-2 bg-[#C5A059] hover:bg-[#B38F4E] text-white" size="sm" data-testid="agent-login-save">{detail.has_login ? "Reset Login" : "Create Login"}</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
